import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Plus, Trash2, Edit } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (currentUser?.role === 'Administrador') {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role]);

  const loadUsers = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/users`);
      setUsers(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/users/${userId}`);
      loadUsers();
    } catch (error) {
      alert('Error al eliminar el usuario');
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const getRoleBadge = (role) => {
    const badges = {
      Administrador: 'bg-purple-100 text-purple-800',
      Colaborador: 'bg-blue-100 text-blue-800',
    };
    return badges[role] || 'bg-slate-100 text-slate-800';
  };

  const getRolePermissions = (role) => {
    const permissions = {
      Administrador: ['Acceso completo', 'Crear/editar/eliminar todo', 'Gestión de usuarios', 'Configuración'],
      Colaborador: ['Ver clientes asignados', 'Cambiar estado de tareas y vencimientos', 'Subir archivos'],
    };
    return permissions[role] || [];
  };

  if (currentUser?.role !== 'Administrador') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Acceso restringido a administradores</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Cargando usuarios...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl tracking-tight font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Usuarios
          </h1>
          <p className="text-sm text-slate-700 leading-relaxed mt-2">Gestión de usuarios y permisos</p>
        </div>
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#1F3864] hover:bg-[#172b4d] text-white"
              data-testid="create-user-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Usuario
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Crear Nuevo Usuario</DialogTitle>
            </DialogHeader>
            <UserForm onSuccess={() => { setShowCreateModal(false); loadUsers(); }} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Users Table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs font-semibold tracking-wider uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-6 py-3">Nombre</th>
                <th className="px-6 py-3">Email</th>
                <th className="px-6 py-3">Rol</th>
                <th className="px-6 py-3">Permisos</th>
                <th className="px-6 py-3">Fecha Creación</th>
                <th className="px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="text-sm text-slate-700">
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-slate-500">
                    No hay usuarios
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user._id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-6 py-4 font-medium text-slate-900">{user.name}</td>
                    <td className="px-6 py-4">{user.email}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadge(user.role)}`}>
                        {user.role}
                      </span>
                      {(user.role === 'Administrador' || user.acceso_conversiones) && (
                        <span className="ml-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200" data-testid={`user-conversiones-badge-${user._id}`}>
                          Conversiones
                        </span>
                      )}
                      {(user.role === 'Administrador' || user.acceso_tributario) && (
                        <span className="ml-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-sky-50 text-sky-700 border border-sky-200" data-testid={`user-tributario-badge-${user._id}`}>
                          Tributario
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {getRolePermissions(user.role).slice(0, 2).map((perm, idx) => (
                          <span key={idx} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                            {perm}
                          </span>
                        ))}
                        {getRolePermissions(user.role).length > 2 && (
                          <span className="text-xs text-slate-500">+{getRolePermissions(user.role).length - 2}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {new Date(user.created_at).toLocaleDateString('es-CO')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(user)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          data-testid={`edit-user-${user._id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {currentUser._id !== user._id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(user._id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-user-${user._id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
          </DialogHeader>
          <UserForm 
            user={selectedUser} 
            onSuccess={() => { setShowEditModal(false); setSelectedUser(null); loadUsers(); }} 
          />
        </DialogContent>
      </Dialog>

      {/* Role Permissions Info */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>Permisos por Rol</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {['Administrador', 'Contador', 'Auxiliar Contable', 'Asistente Contable'].map(role => (
            <div key={role} className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadge(role)}`}>
                  {role}
                </span>
              </div>
              <ul className="text-sm text-slate-600 space-y-1">
                {getRolePermissions(role).map((perm, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-0.5">&#10003;</span>
                    <span>{perm}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// User Form Component
const UserForm = ({ user, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'Auxiliar Contable',
    acceso_conversiones: user?.acceso_conversiones || false,
    acceso_tributario: user?.acceso_tributario || false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = { ...formData };
      if (user) {
        // Edit mode - only send password if changed
        if (!payload.password) {
          delete payload.password;
        }
        await axios.put(`${BACKEND_URL}/api/users/${user._id}`, payload);
      } else {
        // Create mode
        await axios.post(`${BACKEND_URL}/api/users`, payload);
      }
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al guardar el usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-form">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
          {error}
        </div>
      )}

      <div>
        <Label htmlFor="name" className="text-xs font-semibold tracking-wider uppercase text-slate-500">
          Nombre *
        </Label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="mt-1"
          placeholder="Juan Pérez"
        />
      </div>

      <div>
        <Label htmlFor="email" className="text-xs font-semibold tracking-wider uppercase text-slate-500">
          Email *
        </Label>
        <Input
          id="email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="mt-1"
          placeholder="usuario@contadesk.co"
          disabled={!!user}
        />
      </div>

      <div>
        <Label htmlFor="password" className="text-xs font-semibold tracking-wider uppercase text-slate-500">
          Contraseña {user ? '(dejar vacío para mantener actual)' : '*'}
        </Label>
        <Input
          id="password"
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          required={!user}
          className="mt-1"
          placeholder="••••••••"
        />
      </div>

      <div>
        <Label htmlFor="role" className="text-xs font-semibold tracking-wider uppercase text-slate-500">
          Rol *
        </Label>
        <select
          id="role"
          name="role"
          value={formData.role}
          onChange={handleChange}
          className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm mt-1"
          required
        >
          <option value="Administrador">Administrador</option>
          <option value="Colaborador">Colaborador</option>
        </select>
        <p className="text-xs text-slate-500 mt-1">
          {formData.role === 'Administrador' && 'Acceso completo al sistema'}
          {formData.role === 'Colaborador' && 'Ve solo clientes asignados, puede cambiar estado de tareas y vencimientos'}
        </p>
      </div>

      <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer hover:bg-slate-50">
        <input
          type="checkbox"
          name="acceso_conversiones"
          checked={formData.role === 'Administrador' ? true : formData.acceso_conversiones}
          disabled={formData.role === 'Administrador'}
          onChange={(e) => setFormData((prev) => ({ ...prev, acceso_conversiones: e.target.checked }))}
          className="mt-0.5 h-4 w-4 accent-[#1F3864]"
          data-testid="user-form-acceso-conversiones"
        />
        <span>
          <span className="block text-sm font-medium text-slate-800">Acceso al módulo Conversiones</span>
          <span className="block text-xs text-slate-500">
            {formData.role === 'Administrador'
              ? 'Los administradores siempre tienen acceso'
              : 'Permite usar las herramientas de conversión (Siigo, DIAN, RST, Causación)'}
          </span>
        </span>
      </label>

      <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer hover:bg-slate-50">
        <input
          type="checkbox"
          name="acceso_tributario"
          checked={formData.role === 'Administrador' ? true : formData.acceso_tributario}
          disabled={formData.role === 'Administrador'}
          onChange={(e) => setFormData((prev) => ({ ...prev, acceso_tributario: e.target.checked }))}
          className="mt-0.5 h-4 w-4 accent-[#1F3864]"
          data-testid="user-form-acceso-tributario"
        />
        <span>
          <span className="block text-sm font-medium text-slate-800">Acceso al módulo Tributario</span>
          <span className="block text-xs text-slate-500">
            {formData.role === 'Administrador'
              ? 'Los administradores siempre tienen acceso'
              : 'Permite gestionar liquidadores (350, Renta, Patrimonio) y sus declaraciones'}
          </span>
        </span>
      </label>

      <div className="flex justify-end pt-4">
        <Button
          type="submit"
          disabled={loading}
          className="bg-[#1F3864] hover:bg-[#172b4d] text-white"
          data-testid="submit-user-form"
        >
          {loading ? (user ? 'Actualizando...' : 'Creando...') : (user ? 'Actualizar Usuario' : 'Crear Usuario')}
        </Button>
      </div>
    </form>
  );
};

export default Users;
